import axios from "axios";
import { logger } from "./logService";
import { getStoredToken } from "../utils/tokenUtils";
import { injectClientIdIntoConfig } from "../utils/clientContext";
import { installApiLoadingInterceptors } from "../utils/apiLoading";
import {
  API_BASE_PREFIXED,
  addFallbackInterceptor,
} from "../config/api.config";
import { API } from "../config/api.paths";

/**
 * Base API service for handling all API requests
 */
class ApiService {
  constructor() {
    // Use prefixed URLs (new standard)
    this.baseURL = API_BASE_PREFIXED.backend;
    this.userServiceURL = API_BASE_PREFIXED.user;

    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // User service client
    this.userServiceClient = axios.create({
      baseURL: this.userServiceURL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add fallback interceptors for backward compatibility
    addFallbackInterceptor(
      this.client,
      API_BASE_PREFIXED.backend,
      process.env.REACT_APP_API_URL || "http://localhost:3000",
    );
    addFallbackInterceptor(
      this.userServiceClient,
      API_BASE_PREFIXED.user,
      process.env.REACT_APP_USER_SERVICE_URL || "http://localhost:3001",
    );

    // Add auth token to user service requests (timezone comes from DB on the server)
    this.userServiceClient.interceptors.request.use(
      (config) => {
        const token = getStoredToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return injectClientIdIntoConfig(config);
      },
      (error) => Promise.reject(error),
    );

    // Add request interceptor (timezone comes from DB on the server)
    this.client.interceptors.request.use(
      (config) => {
        const token = getStoredToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return injectClientIdIntoConfig(config);
      },
      (error) => {
        logger.error(error, "Request configuration error");
        return Promise.reject(error);
      },
    );

    // Add response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        logger.handleApiError(error);
        return Promise.reject(error);
      },
    );

    installApiLoadingInterceptors(this.client);
    installApiLoadingInterceptors(this.userServiceClient);
  }

  /**
   * Make a POST request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request payload
   * @param {Object} options - Additional axios options
   * @returns {Promise<any>}
   */
  async post(endpoint, data = {}, options = {}) {
    try {
      const response = await this.client.post(endpoint, data, options);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get dashboard statistics
   * @param {Object} filters - Optional filter criteria
   * @returns {Promise<Object>}
   */
  async getDashboardStats(filters = {}) {
    return this.post("dashboard/stats", { filters });
  }

  /**
   * Validate client ID by fetching client details
   * @param {string} clientId - Client ID to validate
   * @returns {Promise<Object>}
   */
  async validateClientId(clientId) {
    return this.post("admin/clients/get", { client_id: clientId });
  }

  /**
   * Get project types
   * @returns {Promise<Array>}
   */
  async getProjectTypes() {
    try {
      const response = await this.post(`projects/types`);
      console.log("Project types API response:", response);
      // Response structure: { success: true, message: { data: [...] }, data: null }
      const types = response.message?.data || response.data || [];
      console.log("Extracted project types:", types);
      return types;
    } catch (error) {
      console.error("Error fetching project types:", error);
      logger.error(error, "Failed to fetch project types");
      // Return fallback project types if API fails
      return [
        "Web Application",
        "Mobile Application",
        "Desktop Application",
        "API Service",
        "Database Project",
        "Integration Project",
        "DevOps Project",
        "Security Testing",
        "Performance Testing",
        "Other",
      ];
    }
  }

  /**
   * Get folder structure
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>}
   */
  async getFolderStructure(projectId) {
    return this.post(API.backend.testcases.search(), {
      project_id: projectId,
      type: "folder",
    });
  }

  /**
   * Create folder
   * @param {Object} folderData - Folder data
   * @returns {Promise<Object>}
   */
  async createFolder(folderData) {
    const { projectId, folderName, parentFolderId } = folderData;
    return this.post(API.backend.testcases.create(), {
      project_id: projectId,
      name: folderName,
      parent_id: parentFolderId,
      type: "folder",
    });
  }

  /**
   * Delete folder
   * @param {Object} folderData - Folder data
   * @returns {Promise<Object>}
   */
  async deleteFolder(folderData) {
    const { projectId, folderId } = folderData;
    return this.post(API.backend.testcases.delete(), {
      project_id: projectId,
      id: folderId,
      type: "folder",
    });
  }

  /**
   * Get role mappings
   * @returns {Promise<Object>}
   */
  async getRoleMappings() {
    const response = await this.userServiceClient.post("/user/user/roles");
    return response.data;
  }

  /**
   * Get user profile from user service
   * @returns {Promise<Object>}
   */
  async getUserProfile() {
    const response = await this.userServiceClient.post(API.user.profile.me());
    return response.data;
  }

  /**
   * Update user profile
   * @param {string} userId - User ID
   * @param {Object|FormData} userData - User data to update
   * @param {Object} options - Additional options including onUploadProgress
   * @returns {Promise<Object>}
   */
  async updateUser(userId, userData, options = {}) {
    // Handle FormData for file uploads
    if (userData instanceof FormData) {
      const config = {
        headers: { "Content-Type": "multipart/form-data" },
      };
      if (options.onUploadProgress) {
        config.onUploadProgress = options.onUploadProgress;
      }
      // Add user_id to FormData if not already present
      if (!userData.has("user_id")) {
        userData.append("user_id", userId);
      }
      const response = await this.userServiceClient.post(
        "/api/v1/users/update",
        userData,
        config,
      );
      return response.data;
    }
    // Regular JSON update
    const response = await this.userServiceClient.post("/api/v1/users/update", {
      user_id: userId,
      ...userData,
    });
    return response.data;
  }

  /**
   * Change password
   * @param {Object} passwordData - Current and new password
   * @returns {Promise<Object>}
   */
  async changePassword(passwordData) {
    const response = await this.userServiceClient.post(
      "/change-password",
      passwordData,
    );
    return response.data;
  }

  /**
   * Logout user
   * @returns {Promise<Object>}
   */
  async logout() {
    const response = await this.userServiceClient.post(API.user.auth.logout());
    return response.data;
  }

  /**
   * Get validation rules
   * @param {string} formType - Type of form
   * @returns {Promise<Object>}
   */
  async getValidationRules(formType) {
    return this.post("validation/rules", { form_type: formType });
  }

  /**
   * Get sorted data
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Request parameters
   * @param {Object} params.sort - Sort criteria
   * @param {Object} params.filters - Additional filters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @returns {Promise<Object>}
   */
  async getSortedData(
    endpoint,
    { sort, filters = {}, page = 1, limit = 10, extra = {} },
  ) {
    return this.post(endpoint, {
      ...extra,
      ...sort,
      ...filters,
      page,
      limit,
    });
  }

  /**
   * Create test case
   * @param {Object} testCaseData - Test case data
   * @returns {Promise<Object>}
   */
  async createTestCase(testCaseData) {
    const {
      projectId,
      folderId,
      title,
      description,
      preconditions,
      expected_result,
      status,
      priority,
    } = testCaseData;
    return this.post(API.backend.testcases.create(), {
      project_id: projectId,
      parent_id: folderId,
      title,
      description,
      preconditions,
      expected_result,
      status,
      priority,
      type: "test_case",
    });
  }
}

// Export singleton instance
export const api = new ApiService();
